import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import baseAssetTags from '../tracking/assetTagRegistry.json';
import extraAssetTags from '../tracking/assetTagRegistryExtras.json';
import { FIRELINE_MEDIC_LOGO_DATA_URI } from './firelineMedicLogoData';
import {
  nativeNfcAvailable,
  scanNativeGearTags,
  writeNativeGearTag,
} from '../native/nativeNfc';
import { getMemberAccessToken } from '../auth/supabase';
import { canonicalWebOrigin, isNativeShell } from '../auth/authRedirect';
import './admin.css';

const assetTags = [...baseAssetTags, ...extraAssetTags];

const CATEGORY_LABELS = {
  'Line Medic Pack': 'Line Medic Packs',
  'Line EMT BLS Pack': 'Line EMT BLS Packs',
  'Trauma Bag': 'Trauma Bags',
  'O2 Bag': 'O2 Bags',
  'AED': 'AEDs',
  'Radio Antenna': 'Radio Antennas',
  'Clipboard': 'Clipboards',
  'Combi Tool': 'Combi Tools',
  'Ice Chest': 'Ice Chests',
  'Radio Clamshell': 'Radio Clamshells',
};

const CATEGORY_ORDER = [
  'Line Medic Pack',
  'Line EMT BLS Pack',
  'Trauma Bag',
  'O2 Bag',
  'AED',
  'Radio Antenna',
  'Clipboard',
  'Combi Tool',
  'Ice Chest',
  'Radio Clamshell',
];

const SINGLE_SIDED_CATEGORIES = new Set(['Clipboard', 'Combi Tool', 'Radio Clamshell']);
const COMPACT_CATEGORIES = new Set(['Combi Tool', 'Radio Clamshell']);

function tagProfile(category) {
  const compact = COMPACT_CATEGORIES.has(category);
  const singleSided = SINGLE_SIDED_CATEGORIES.has(category);
  return {
    compact,
    singleSided,
    copies: singleSided ? 1 : 2,
    pageCapacity: compact ? 16 : 8,
    sizeLabel: compact ? '1.5 × 2.25 inch compact tag' : '2 × 3.5 inch insert',
  };
}

function supportsWebNfc() {
  return typeof window !== 'undefined' && 'NDEFReader' in window;
}

function assetMark(asset) {
  const id = String(asset?.id || '').trim().toUpperCase();
  if (id === 'LM-PACK-GREG') return 'GREG';
  const match = id.match(/-(\d{2})$/);
  if (match) return match[1];
  const tail = id.split('-').filter(Boolean).pop();
  return tail || 'GEAR';
}

function categoryTitle(asset) {
  return String(asset?.category || 'PROGRAM GEAR').trim().toUpperCase();
}

function chunk(items, size) {
  const pages = [];
  for (let index = 0; index < items.length; index += size) pages.push(items.slice(index, index + size));
  return pages;
}

function AssetTagCard({ asset, qrDataUrl, copyIndex = 1 }) {
  if (!asset) return null;
  const mark = assetMark(asset);
  const title = categoryTitle(asset);
  const profile = tagProfile(asset.category);
  const isLongTitle = title.length > 14;

  return (
    <div className={`asset-tag-card${profile.compact ? ' is-compact' : ''}`} aria-label={`${asset.id} print copy ${copyIndex}`}>
      <img className="asset-tag-logo" src={FIRELINE_MEDIC_LOGO_DATA_URI} alt="Fireline Medic Sac Metro Fire" />
      <div className="asset-tag-agency">SAC METRO FIRE</div>
      <div className={`asset-tag-title ${isLongTitle ? 'is-long' : ''}`}>{title}</div>
      <div className={`asset-tag-mark ${mark.length > 2 ? 'is-name' : ''}`}>{mark}</div>
      <div className="asset-tag-id">{asset.id}</div>
      <div className="asset-tag-rule" />
      <div className="asset-tag-scan">TAP OR SCAN</div>
      {qrDataUrl
        ? <img className="asset-tag-qr" src={qrDataUrl} alt={`QR code for ${asset.id}`} />
        : <div className="asset-tag-qr-placeholder" />}
      <div className="asset-tag-found">CALL IF FOUND</div>
      <div className="asset-tag-phone">{asset.phone}</div>
      {!profile.compact && <div className="asset-tag-nfc">NFC INSIDE • {asset.kind === 'personal' ? 'PERSONAL GEAR' : 'PROGRAM GEAR'}</div>}
    </div>
  );
}

export default function AssetTagSetup({ profile }) {
  const categoryOptions = useMemo(
    () => CATEGORY_ORDER
      .filter(category => assetTags.some(item => item.category === category))
      .map(category => ({
        value: category,
        label: CATEGORY_LABELS[category] || category,
        count: assetTags.filter(item => item.category === category).length,
      })),
    [],
  );

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [bulkIds, setBulkIds] = useState([]);
  const [printMode, setPrintMode] = useState('single');
  const [message, setMessage] = useState('');
  const [nfcSelectedId, setNfcSelectedId] = useState('');
  const [nfcMessage, setNfcMessage] = useState('');
  const [writing, setWriting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [qrById, setQrById] = useState({});
  const [baseUrl, setBaseUrl] = useState('');
  const [baseUrlSource, setBaseUrlSource] = useState('');
  const [baseUrlWarning, setBaseUrlWarning] = useState('');

  const categoryAssets = useMemo(
    () => selectedCategory ? assetTags.filter(item => item.category === selectedCategory) : [],
    [selectedCategory],
  );

  const selectedCategoryLabel = CATEGORY_LABELS[selectedCategory] || selectedCategory;
  const selectedProfile = tagProfile(selectedCategory);
  const asset = useMemo(
    () => categoryAssets.find(item => item.id === selectedId) || categoryAssets[0] || null,
    [categoryAssets, selectedId],
  );
  const assetUrl = baseUrl && asset ? `${baseUrl}/gear/${encodeURIComponent(asset.id)}` : '';
  const qrDataUrl = asset ? qrById[asset.id] || '' : '';

  const nfcAsset = useMemo(
    () => categoryAssets.find(item => item.id === nfcSelectedId) || categoryAssets[0] || null,
    [categoryAssets, nfcSelectedId],
  );
  const nfcAssetUrl = baseUrl && nfcAsset
    ? `${baseUrl}/gear/${encodeURIComponent(nfcAsset.id)}`
    : '';

  useEffect(() => {
    let cancelled = false;
    getMemberAccessToken()
      .then(token => fetch('/api/asset-tag-config', {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }))
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.baseUrl) throw new Error(data?.error || 'Canonical asset-tag URL is unavailable.');
        if (!cancelled) {
          setBaseUrl(String(data.baseUrl).replace(/\/+$/, ''));
          setBaseUrlSource(data.source || 'configured');
          setBaseUrlWarning('');
        }
      })
      .catch(error => {
        if (cancelled) return;
        const native = isNativeShell();
        const fallback = native
          ? canonicalWebOrigin()
          : (typeof window !== 'undefined'
              ? window.location.origin.replace(/\/+$/, '')
              : canonicalWebOrigin());

        setBaseUrl(fallback);
        setBaseUrlSource(native ? 'native canonical fallback' : 'browser fallback');
        setBaseUrlWarning(
          native
            ? `${error?.message || 'Canonical URL check failed.'} Using the canonical production app URL.`
            : `${error?.message || 'Canonical URL check failed.'} Using the current site origin; verify it is the production site before printing.`
        );
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setQrById({});
    if (!baseUrl || !categoryAssets.length) return undefined;

    Promise.all(categoryAssets.map(async item => {
      const url = `${baseUrl}/gear/${encodeURIComponent(item.id)}`;
      const qr = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 720,
        color: { dark: '#000000', light: '#ffffff' },
      });
      return [item.id, qr];
    })).then(entries => {
      if (!cancelled) setQrById(Object.fromEntries(entries));
    }).catch(() => {
      if (!cancelled) setQrById({});
    });

    return () => { cancelled = true; };
  }, [baseUrl, categoryAssets]);

  if (!profile?.is_admin) {
    return (
      <div className="admin-root">
        <main className="admin-page">
          <section className="admin-card admin-denied"><h1>Admin access required</h1></section>
        </main>
      </div>
    );
  }

  const chooseCategory = value => {
    const nextAssets = value ? assetTags.filter(item => item.category === value) : [];
    setSelectedCategory(value);
    setSelectedId(nextAssets[0]?.id || '');
    setNfcSelectedId(nextAssets[0]?.id || '');
    setBulkIds(nextAssets.map(item => item.id));
    setPrintMode('single');
    setMessage('');
    setNfcMessage('');
  };

  const copyUrl = async () => {
    if (!nfcAssetUrl || !nfcAsset) return;

    try {
      await navigator.clipboard.writeText(nfcAssetUrl);
      setNfcMessage(`Copied NFC URL for ${nfcAsset.id}.`);
    } catch (_) {
      setNfcMessage('Could not copy automatically. Press and hold the URL below to copy it.');
    }
  };

  const writeNfc = async () => {
    if (!nfcAsset || !nfcAssetUrl) {
      setNfcMessage('Choose an NFC asset before writing.');
      return;
    }

    setWriting(true);
    setNfcMessage(`Hold a writable NFC tag near the top of the iPhone for ${nfcAsset.id}…`);

    try {
      if (nativeNfcAvailable()) {
        await writeNativeGearTag(nfcAssetUrl);
        setNfcMessage(`✓ Programmed as ${nfcAsset.id}. Tap Verify NFC Tag to confirm.`);
        return;
      }

      if (supportsWebNfc()) {
        const ndef = new window.NDEFReader();
        await ndef.write({
          records: [{ recordType: 'url', data: nfcAssetUrl }],
        });
        setNfcMessage(`✓ Programmed as ${nfcAsset.id}.`);
        return;
      }

      setNfcMessage('Direct NFC writing is unavailable here. Copy the URL and use NFC Tools.');
    } catch (error) {
      setNfcMessage(error?.message || 'NFC write failed.');
    } finally {
      setWriting(false);
    }
  };

  const verifyNfc = async () => {
    if (!nfcAsset) return;

    if (!nativeNfcAvailable()) {
      setNfcMessage('NFC verification is available in the native iPhone app.');
      return;
    }

    setVerifying(true);
    setNfcMessage(`Scan the NFC tag to verify ${nfcAsset.id}…`);

    try {
      const ids = await scanNativeGearTags({ allowMultiple: false });
      const scanned = String(ids?.[0] || '').trim().toUpperCase();
      const expected = String(nfcAsset.id || '').trim().toUpperCase();

      if (!scanned) {
        setNfcMessage('No Line Medic NFC tag was detected.');
      } else if (scanned === expected) {
        setNfcMessage(`✓ Verified — tag is ${nfcAsset.id}.`);
      } else {
        setNfcMessage(`⚠ Mismatch — tag contains ${scanned}, but ${nfcAsset.id} is selected.`);
      }
    } catch (error) {
      const msg = error?.message || 'NFC verification failed.';
      setNfcMessage(/cancel/i.test(msg) ? 'NFC verification cancelled.' : msg);
    } finally {
      setVerifying(false);
    }
  };

  const toggleBulk = id => {
    setBulkIds(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  };

  const printSingle = () => {
    setPrintMode('single');
    window.setTimeout(() => window.print(), 80);
  };

  const printBulk = () => {
    if (!bulkIds.length) {
      setMessage(`Select at least one ${selectedCategoryLabel || 'asset'} for bulk printing.`);
      return;
    }
    setPrintMode('bulk');
    window.setTimeout(() => window.print(), 80);
  };

  const selectedBulkAssets = categoryAssets.filter(item => bulkIds.includes(item.id));
  const printAssets = printMode === 'bulk' ? selectedBulkAssets : (asset ? [asset] : []);
  const printFaces = printAssets.flatMap(item => Array.from(
    { length: tagProfile(item.category).copies },
    (_, index) => ({ asset: item, copyIndex: index + 1 }),
  ));
  const printPages = chunk(printFaces, selectedProfile.pageCapacity);
  const allQrReady = printAssets.length > 0 && printAssets.every(item => Boolean(qrById[item.id]));
  const bulkFaceCount = bulkIds.length * selectedProfile.copies;
  const bulkSheetCount = Math.ceil(bulkFaceCount / selectedProfile.pageCapacity) || 0;
  const categoryQrReady = categoryAssets.length > 0 && categoryAssets.every(item => Boolean(qrById[item.id]));

  return (
    <div className="admin-root asset-tag-setup-root">
      <style>{`
        .asset-tag-preview-wrap{display:grid;justify-items:center;gap:14px;margin-top:14px;padding:22px;border-radius:18px;background:#f2f0e9;color:#111;overflow:auto}
        .asset-tag-print-sheet{display:none}
        .asset-tag-card{box-sizing:border-box;width:2in;height:3.5in;background:#fff;color:#213019;border:2px solid #26351d;padding:.055in .075in .055in;display:flex;flex-direction:column;align-items:center;text-align:center;overflow:hidden;font-family:Arial,Helvetica,sans-serif;print-color-adjust:exact;-webkit-print-color-adjust:exact}
        .asset-tag-logo{width:.62in;height:.62in;object-fit:contain;display:block;margin:0 0 .005in}
        .asset-tag-agency{font-size:6.1pt;line-height:1;font-weight:900;letter-spacing:1px;color:#26351d;white-space:nowrap;margin-top:-.002in}
        .asset-tag-title{font-size:9.2pt;line-height:1;font-weight:950;letter-spacing:.25px;white-space:nowrap;color:#26351d;margin-top:.02in}
        .asset-tag-title.is-long{font-size:7.2pt;letter-spacing:.12px}
        .asset-tag-mark{font-size:34pt;line-height:.88;font-weight:950;letter-spacing:-1.2px;color:#eb6208;margin:.025in 0 .005in}
        .asset-tag-mark.is-name{font-size:18pt;line-height:1;font-weight:950;letter-spacing:.6px;margin:.07in 0 .045in}
        .asset-tag-id{font-size:7.2pt;line-height:1;font-weight:950;letter-spacing:.25px;white-space:nowrap;color:#26351d}
        .asset-tag-rule{width:94%;height:2px;background:#dba628;margin:.04in 0 .025in}
        .asset-tag-scan{font-size:6.7pt;line-height:1;font-weight:950;letter-spacing:.9px;color:#26351d;margin-bottom:.018in}
        .asset-tag-qr,.asset-tag-qr-placeholder{width:1.12in;height:1.12in;display:block;object-fit:contain;background:#fff}
        .asset-tag-found{font-size:6.1pt;line-height:1;font-weight:950;letter-spacing:.6px;color:#eb6208;margin-top:.018in}
        .asset-tag-phone{font-size:9.4pt;line-height:1;font-weight:950;color:#213019;margin-top:.012in;white-space:nowrap}
        .asset-tag-nfc{font-size:4.8pt;line-height:1.05;font-weight:850;letter-spacing:.4px;color:#59654c;margin-top:auto;padding-top:.02in;white-space:nowrap}
        .asset-tag-card.is-compact{width:1.5in;height:2.25in;border-width:1.5px;padding:.035in .045in}
        .asset-tag-card.is-compact .asset-tag-logo{width:.36in;height:.36in;margin:0}
        .asset-tag-card.is-compact .asset-tag-agency{font-size:4.5pt;letter-spacing:.55px}
        .asset-tag-card.is-compact .asset-tag-title{font-size:6.2pt;margin-top:.012in;letter-spacing:.1px}
        .asset-tag-card.is-compact .asset-tag-title.is-long{font-size:5.5pt}
        .asset-tag-card.is-compact .asset-tag-mark{font-size:20pt;line-height:.9;margin:.018in 0 0}
        .asset-tag-card.is-compact .asset-tag-id{font-size:5.2pt;letter-spacing:.05px}
        .asset-tag-card.is-compact .asset-tag-rule{height:1px;margin:.022in 0 .015in}
        .asset-tag-card.is-compact .asset-tag-scan{font-size:4.7pt;letter-spacing:.45px;margin-bottom:.008in}
        .asset-tag-card.is-compact .asset-tag-qr,.asset-tag-card.is-compact .asset-tag-qr-placeholder{width:.78in;height:.78in}
        .asset-tag-card.is-compact .asset-tag-found{font-size:4.3pt;margin-top:.01in}
        .asset-tag-card.is-compact .asset-tag-phone{font-size:6.5pt;margin-top:.006in}
        .asset-tag-preview-wrap .asset-tag-card{box-shadow:0 10px 28px rgba(0,0,0,.16)}
        .asset-tag-category-picker{margin-top:14px;padding:18px;border:1px solid rgba(217,164,65,.25);border-radius:16px;background:rgba(217,164,65,.055)}
        .asset-tag-category-picker select{width:100%;padding:15px;border-radius:12px;font-size:17px;margin-top:8px}
        .asset-tag-bulk-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:10px;margin-top:12px}
        .asset-tag-bulk-option{display:flex;align-items:flex-start;gap:10px;padding:12px;border:1px solid rgba(255,255,255,.12);border-radius:12px;cursor:pointer}
        .asset-tag-bulk-option input{margin-top:3px;transform:scale(1.15)}
        @media print{
          @page{size:letter portrait;margin:.25in}
          body{background:#fff!important}
          body *{visibility:hidden!important}
          .asset-tag-print-sheet,.asset-tag-print-sheet *{visibility:visible!important}
          .asset-tag-print-sheet{display:block!important;position:absolute;left:0;top:0;width:100%}
          .asset-tag-print-page{display:grid;grid-template-columns:repeat(3,2in);grid-auto-rows:3.5in;column-gap:.15in;row-gap:0;align-content:start;width:6.3in;min-height:10.5in;break-after:page;page-break-after:always}
          .asset-tag-print-page.is-compact{grid-template-columns:repeat(4,1.5in);grid-auto-rows:2.25in;column-gap:.12in;row-gap:.12in;width:6.36in}
          .asset-tag-print-page:last-child{break-after:auto;page-break-after:auto}
          .asset-tag-card{box-shadow:none!important;break-inside:avoid;page-break-inside:avoid}
        }
      `}</style>

      <header className="admin-topbar">
        <div>
          <h1>Asset Tag Setup</h1>
          <p>Registered QR + NFC identities and exact-size gear tags</p>
        </div>
        <button type="button" onClick={() => window.location.assign('/admin')}>Admin</button>
      </header>

      <main className="admin-page">
        <section className="admin-card" style={{ maxWidth: 820, margin: '0 auto' }}>
          <div className="admin-hub-kicker">REGISTERED TAGS</div>
          <h2 style={{ marginTop: 8 }}>Build / replace asset tags</h2>

          <div className="asset-tag-category-picker">
            <div style={{ opacity: .68, fontSize: 12, fontWeight: 800, letterSpacing: 1.2 }}>CHOOSE CATEGORY</div>
            <select value={selectedCategory} onChange={event => chooseCategory(event.target.value)}>
              <option value="">Choose Category…</option>
              {categoryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label} · {option.count}
                </option>
              ))}
            </select>
            {!selectedCategory && (
              <p style={{ marginBottom: 0, opacity: .74, lineHeight: 1.5 }}>
                Choose a gear category first. The tag builder will then show only the physical assets registered in that category.
              </p>
            )}
          </div>

          {selectedCategory && (
            <>
              <h3 style={{ margin: '22px 0 4px' }}>{selectedCategoryLabel}</h3>
              <div style={{ opacity: .68, fontSize: 13 }}>
                {categoryAssets.length} registered physical asset{categoryAssets.length === 1 ? '' : 's'} · {selectedProfile.sizeLabel} · {selectedProfile.singleSided ? 'single-sided' : 'two matching faces'}
              </div>
              <select
                value={asset?.id || ''}
                onChange={event => { setSelectedId(event.target.value); setMessage(''); setPrintMode('single'); }}
                style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 17, marginTop: 10 }}
              >
                {categoryAssets.map(item => <option key={item.id} value={item.id}>{item.name} · {item.id}</option>)}
              </select>
            </>
          )}

          {asset && (
            <div style={{ marginTop: 22, display: 'grid', gap: 14 }}>
              <div style={{ padding: 18, borderRadius: 16, border: '1px solid rgba(255,255,255,.12)' }}>
                <div style={{ opacity: .68, fontSize: 12, fontWeight: 800, letterSpacing: 1.2 }}>ASSET ID</div>
                <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4 }}>{asset.id}</div>
                <div style={{ marginTop: 8 }}>{asset.name}</div>
                <div style={{ marginTop: 4, opacity: .7 }}>{asset.category}</div>
                <div style={{ marginTop: 8, opacity: .76 }}>Recovery contact: {asset.contactName} · {asset.phone}</div>
              </div>

              <div style={{ padding: 18, borderRadius: 16, border: '1px solid rgba(255,255,255,.12)' }}>
                <div style={{ opacity: .68, fontSize: 12, fontWeight: 800, letterSpacing: 1.2 }}>{selectedProfile.sizeLabel.toUpperCase()} · {selectedProfile.singleSided ? 'SINGLE-SIDED' : 'FRONT / BACK PAIR'}</div>
                <p style={{ opacity: .78, lineHeight: 1.5 }}>
                  {selectedProfile.compact
                    ? 'Compact tags are intended for direct attachment to small field gear. Print at 100% / Actual Size.'
                    : selectedProfile.singleSided
                      ? 'This category only needs one printed face. Print at 100% / Actual Size.'
                      : 'The 2 × 3.5 insert is sized for the existing 2.5 × 4.25 laminate pouch and prints two matching faces.'}
                </p>
                <div className="asset-tag-preview-wrap">
                  <AssetTagCard asset={asset} qrDataUrl={qrDataUrl} />
                  <button type="button" onClick={printSingle} disabled={!qrDataUrl}>
                    Print This Tag · {selectedProfile.copies === 1 ? '1 Copy' : '2 Copies'}
                  </button>
                </div>
              </div>

              <div style={{ padding: 18, borderRadius: 16, border: '1px solid rgba(255,255,255,.12)' }}>
                <div style={{ opacity: .68, fontSize: 12, fontWeight: 800, letterSpacing: 1.2 }}>BULK PRINT · {selectedCategoryLabel.toUpperCase()}</div>
                <p style={{ opacity: .78, lineHeight: 1.5 }}>
                  {selectedProfile.compact
                    ? 'Letter-size pages hold up to sixteen compact single-sided tags.'
                    : selectedProfile.singleSided
                      ? 'Each selected asset prints once; no duplicate back face is generated.'
                      : 'Each selected asset prints two matching faces for the laminate pouch.'}
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setBulkIds(categoryAssets.map(item => item.id))}>Select All {selectedCategoryLabel}</button>
                  <button type="button" onClick={() => setBulkIds([])}>Clear</button>
                </div>

                <div className="asset-tag-bulk-grid">
                  {categoryAssets.map(item => (
                    <label key={item.id} className="asset-tag-bulk-option">
                      <input type="checkbox" checked={bulkIds.includes(item.id)} onChange={() => toggleBulk(item.id)} />
                      <span><strong>{item.name}</strong><br /><span style={{ opacity: .7 }}>{item.id}</span></span>
                    </label>
                  ))}
                </div>

                <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={printBulk}
                    disabled={!bulkIds.length || !baseUrl || !categoryQrReady}
                  >
                    Bulk Print Selected · {selectedProfile.copies === 1 ? '1 Copy Each' : '2 Copies Each'}
                  </button>
                  <span style={{ opacity: .72, fontSize: 13 }}>
                    {bulkIds.length} physical tag{bulkIds.length === 1 ? '' : 's'} · {bulkFaceCount} printed face{bulkFaceCount === 1 ? '' : 's'} · {bulkSheetCount} sheet{bulkSheetCount === 1 ? '' : 's'}
                  </span>
                </div>
              </div>

              <div style={{ padding: 18, borderRadius: 16, border: '1px solid rgba(255,255,255,.12)' }}>
                <div style={{ opacity: .68, fontSize: 12, fontWeight: 800, letterSpacing: 1.2 }}>REGISTERED QR</div>
                <p style={{ opacity: .78, lineHeight: 1.5 }}>This QR is generated locally by our app from the permanent production gear URL below. No third-party QR service or redirect is involved.</p>
                {qrDataUrl && (
                  <div style={{ display: 'grid', justifyItems: 'center', gap: 12 }}>
                    <img src={qrDataUrl} alt={`QR code for ${asset.id}`} style={{ width: 'min(320px, 100%)', background: '#fff', borderRadius: 12 }} />
                    <a href={qrDataUrl} download={`${asset.id}-QR.png`} className="back-button" style={{ textDecoration: 'none' }}>Download QR PNG</a>
                  </div>
                )}
              </div>

              <div
                style={{
                  padding: 18,
                  borderRadius: 16,
                  border: '1px solid rgba(98,215,143,.28)',
                  background: 'rgba(98,215,143,.045)',
                }}
              >
                <div style={{
                  opacity: .68,
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 1.2,
                }}>
                  NFC PROGRAMMING · {selectedCategoryLabel.toUpperCase()}
                </div>

                <h3 style={{ margin: '10px 0 5px' }}>
                  Program a physical NFC tag
                </h3>

                <p style={{ opacity: .78, lineHeight: 1.5 }}>
                  This selection is separate from the QR/print asset above.
                  Choose exactly which physical asset the NFC chip should identify.
                </p>

                <label style={{
                  display: 'grid',
                  gap: 7,
                  marginTop: 16,
                  fontWeight: 800,
                  fontSize: 13,
                }}>
                  NFC asset

                  <select
                    value={nfcAsset?.id || ''}
                    onChange={event => {
                      setNfcSelectedId(event.target.value);
                      setNfcMessage('');
                    }}
                    style={{
                      width: '100%',
                      padding: 14,
                      borderRadius: 12,
                      fontSize: 17,
                    }}
                  >
                    {categoryAssets.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name} · {item.id}
                      </option>
                    ))}
                  </select>
                </label>

                {nfcAsset && (
                  <div style={{
                    marginTop: 16,
                    padding: 14,
                    borderRadius: 13,
                    border: '1px solid rgba(255,255,255,.10)',
                    background: 'rgba(0,0,0,.14)',
                  }}>
                    <div style={{ fontSize: 20, fontWeight: 900 }}>
                      {nfcAsset.name}
                    </div>

                    <div style={{ opacity: .7, marginTop: 4 }}>
                      {nfcAsset.id}
                    </div>

                    <div style={{
                      marginTop: 12,
                      overflowWrap: 'anywhere',
                      fontFamily: 'monospace',
                      fontSize: 13,
                    }}>
                      {nfcAssetUrl || 'Loading production URL…'}
                    </div>

                    {baseUrlSource && (
                      <div style={{ opacity: .55, fontSize: 12, marginTop: 6 }}>
                        URL source: {baseUrlSource}
                      </div>
                    )}

                    {baseUrlWarning && (
                      <div style={{
                        marginTop: 10,
                        padding: 10,
                        borderRadius: 10,
                        background: 'rgba(242,173,75,.12)',
                        color: '#f6c677',
                      }}>
                        {baseUrlWarning}
                      </div>
                    )}
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginTop: 16,
                }}>
                  <button
                    type="button"
                    onClick={writeNfc}
                    disabled={!nfcAssetUrl || writing || verifying}
                  >
                    {writing ? 'Writing…' : 'Write NFC Tag'}
                  </button>

                  <button
                    type="button"
                    onClick={verifyNfc}
                    disabled={!nfcAsset || writing || verifying}
                  >
                    {verifying ? 'Verifying…' : 'Verify NFC Tag'}
                  </button>

                  <button
                    type="button"
                    onClick={copyUrl}
                    disabled={!nfcAssetUrl}
                  >
                    Copy NFC URL
                  </button>

                  <button
                    type="button"
                    disabled={!nfcAssetUrl}
                    onClick={() =>
                      window.open(nfcAssetUrl, '_blank', 'noopener,noreferrer')
                    }
                  >
                    Test Public Page
                  </button>
                </div>

                {!nativeNfcAvailable() && !supportsWebNfc() && (
                  <div style={{
                    marginTop: 16,
                    padding: 14,
                    borderRadius: 12,
                    background: 'rgba(217,164,65,.06)',
                    border: '1px solid rgba(217,164,65,.25)',
                  }}>
                    <strong>Browser fallback</strong>
                    <p style={{ marginBottom: 0 }}>
                      Copy the NFC URL and program it with NFC Tools.
                    </p>
                  </div>
                )}

                {nfcMessage && (
                  <div style={{
                    marginTop: 16,
                    padding: 14,
                    borderRadius: 12,
                    background: 'rgba(255,255,255,.07)',
                  }}>
                    {nfcMessage}
                  </div>
                )}
              </div>

              {message && <div style={{ padding: 14, borderRadius: 12, background: 'rgba(255,255,255,.06)' }}>{message}</div>}
            </div>
          )}
        </section>
      </main>

      <div className="asset-tag-print-sheet" aria-hidden="true">
        {allQrReady && printPages.map((page, pageIndex) => (
          <div className={`asset-tag-print-page${selectedProfile.compact ? ' is-compact' : ''}`} key={`page-${pageIndex}`}>
            {page.map(face => (
              <AssetTagCard
                key={`${face.asset.id}-${face.copyIndex}`}
                asset={face.asset}
                qrDataUrl={qrById[face.asset.id]}
                copyIndex={face.copyIndex}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
